import React, { useState, useImperativeHandle, useEffect, useRef } from 'react';
import { animated, useSpring } from 'react-spring';
import _uniqueId from 'lodash/uniqueId';
import _delay from 'lodash/delay';

import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

import {
  getEasing,
  getRemBaseFontSize,
  getDuration
} from 'common/parse-tokens';

const SIZE = 2 * getRemBaseFontSize();
const DURATION = getDuration(Tokens.SPEED.SPEED_SLOW);

const STYLE = backgroundColor => ({
  pointerEvents: 'none' as const,
  position: 'absolute' as const,
  height: SIZE,
  width: SIZE,
  minWidth: SIZE,
  borderRadius: '100%',
  zIndex: 0,
  backgroundColor
});

const STATE_HIDDEN = {
  opacity: Tokens.OPACITY.OPACITY_0,
  width: 0,
  minWidth: 0,
  height: 0,
};

const STATE_VISIBLE = {
  opacity: Tokens.OPACITY.OPACITY_24,
  width: SIZE,
  height: SIZE,
  minWidth: SIZE,
};

export interface GetVisibleProps extends React.ComponentPropsWithRef<'div'> {
  rippleStyle: React.CSSProperties;
  $theme: Record<string, unknown>;
}

const GetVisible = ({
  rippleStyle,
  $theme
}: GetVisibleProps) => {
  const getVisibleProps = useSpring({
    ...STATE_VISIBLE,
    from: STATE_HIDDEN,
    config: {
      duration: DURATION,
      easing: getEasing(Tokens.TRANSITION.TRANSITION_ACCELERATE)
    }
  });
  return (
    <animated.div
      style={{
        ...STYLE($theme.rippleColor),
        ...getVisibleProps,
        ...rippleStyle
      }}
    />
  );
};

export interface GetHiddenProps extends React.ComponentPropsWithRef<'div'> {
  rippleStyle: React.CSSProperties;
  $theme: Record<string, unknown>;
}

const GetHidden = ({
  rippleStyle,
  $theme
}: GetHiddenProps) => {
  const getHiddenProps = useSpring({
    ...STATE_HIDDEN,
    from: STATE_VISIBLE,
    config: {
      duration: DURATION,
      easing: getEasing(Tokens.TRANSITION.TRANSITION_ACCELERATE)
    }
  });
  return (
    <animated.div
      style={{
        ...STYLE($theme.rippleColor),
        ...getHiddenProps,
        ...rippleStyle
      }}
    />
  );
};

export interface CheckboxRippleProps extends React.ComponentPropsWithoutRef<'div'> {
  $theme?: any;
  rippleStyle?: React.CSSProperties;
}

export type CheckboxRippleHandle = {
  runRipple: () => void;
  asyncHideRipple: () => void;
};

const CheckboxRipple = React.forwardRef<CheckboxRippleHandle, CheckboxRippleProps>((props: CheckboxRippleProps, ref) => {
  const {
    rippleStyle,
    $theme
  } = props;
  const unmount = useRef(false);
  const isDurationOver = useRef(false);
  const isRequestedToHide = useRef(false);
  const [uniqueId, setUniqueId] = useState(_uniqueId());
  const [renderGetHidden, setRenderGetHidden] = useState(false);
  const [renderGetVisible, setRenderGetVisible] = useState(false);
  const stopAnimation = () => {
    setRenderGetHidden(true);
    setRenderGetVisible(false);
    isDurationOver.current = false;
    isRequestedToHide.current = false;
    _delay(() => setRenderGetHidden(false), DURATION);
  };

  useEffect(() => () => { unmount.current = true; }, []);

  const updateEndAnimation = () => {
    if (unmount.current) {
      return;
    }
    if (isRequestedToHide.current) {
      stopAnimation();
    }
    isDurationOver.current = true;
  };

  const runRipple = () => {
    // to refresh the animation
    setUniqueId(_uniqueId());
    setRenderGetHidden(false);
    setRenderGetVisible(true);
    isDurationOver.current = false;
    _delay(updateEndAnimation, DURATION);
  };

  const asyncHideRipple = () => {
    if (unmount.current) {
      return;
    }
    if (isDurationOver.current) {
      stopAnimation();
    } else {
      isRequestedToHide.current = true;
    }
  };

  useImperativeHandle(ref, () => ({
    runRipple,
    asyncHideRipple,
  }));

  return (
    <>
      {renderGetVisible && <GetVisible key={uniqueId} rippleStyle={rippleStyle} $theme={$theme} />}
      {renderGetHidden && <GetHidden rippleStyle={rippleStyle} $theme={$theme} />}
    </>
  );
});

CheckboxRipple.defaultProps = {
  rippleStyle: {},
  $theme: {}
};

CheckboxRipple.displayName = 'CheckboxRipple';
export default CheckboxRipple;

